import { useMemo } from 'react';
import { Observable } from 'rxjs';
import type { Subscription } from 'rxjs';
import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import { debounce, isEmpty } from 'lodash';
import { searchKbs } from 'rest/search';
import type { RequestCreateKBState } from 'state/persisted/request-create';
import type { KnowledgeArticle, KnowledgeBaseSearchResult } from 'state/ui/search';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { sendEvent, trackError } from '@atlassian/help-center-common-util/analytics';
import { isNetworkOrClientErrorCode } from '@atlassian/help-center-common-util/error-codes';
import type { FieldMeta } from '@atlassian/proforma-common-core/form-system-models';

export const useOnFieldChange = (
    createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
    fetchKBSuggestionsSuccess: (args: {
        kbArticleSuggestions: KnowledgeArticle[];
        portalId?: number;
        projectId?: number;
    }) => void,
    knowledgeBase: RequestCreateKBState | undefined,
    portalId: number | undefined,
    projectId: number | undefined,
    subscription: Subscription,
    requestTypeId: number | undefined
) => {
    return useMemo(
        () =>
            debounce((fieldMeta: FieldMeta, value: string) => {
                // Only search for KB articles when the field is linked to Jira summary
                if (fieldMeta.jiraField === 'summary') {
                    const trimmedInputValue = value.trim();
                    const meta = {
                        analyticsSuccessEvent: createAnalyticsEvent?.({
                            action: 'loaded',
                            analyticsType: TRACK_EVENT_TYPE,
                            actionSubject: 'kbSuggestionFieldProforma',
                        }),
                        analyticsFailureEvent: createAnalyticsEvent?.({
                            action: 'failed',
                            analyticsType: TRACK_EVENT_TYPE,
                            actionSubject: 'kbSuggestionFieldProforma',
                        }),
                    };

                    const shouldShowKbSuggestions = FeatureGates.checkGate(
                        'remove-linked-spaces-check-for-article-suggestions'
                    )
                        ? true
                        : !!knowledgeBase;
                    // Stop if the value is empty/only whitespace or there is no knowledge base
                    if (!shouldShowKbSuggestions || isEmpty(trimmedInputValue)) {
                        return;
                    }

                    subscription.add(
                        searchKbs({
                            projectId,
                            labels: knowledgeBase ? knowledgeBase.labels : [],
                            query: trimmedInputValue,
                            resultLimit: 3,
                            requestTypeId: FeatureGates.checkGate('remove-linked-spaces-check-for-article-suggestions')
                                ? requestTypeId
                                : undefined,
                        }).subscribe(
                            (kbSearchResponse: KnowledgeBaseSearchResult) => {
                                if (meta.analyticsSuccessEvent) {
                                    sendEvent(meta.analyticsSuccessEvent);
                                }
                                fetchKBSuggestionsSuccess({
                                    portalId,
                                    kbArticleSuggestions: kbSearchResponse.articles,
                                });
                            },
                            (e) => {
                                // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                if (meta.analyticsFailureEvent && !isNetworkOrClientErrorCode(e.status)) {
                                    sendEvent(meta.analyticsFailureEvent);
                                }
                                trackError('kbsuggestions.fetch.failed', {}, e);
                                return Observable.empty();
                            }
                        )
                    );
                }
            }, USER_TYPING_DEBOUNCE_TIME),
        [
            createAnalyticsEvent,
            fetchKBSuggestionsSuccess,
            knowledgeBase,
            portalId,
            projectId,
            subscription,
            requestTypeId,
        ]
    );
};
